import { formations, playerById } from './data';
import { createDoubleRoundRobinSchedule, createStandings, playerRating, type LeagueGroup, type RivalTeam } from './competition';
import { resolveDevelopedPlayer } from './development';
import { SeededRng } from './simulation/rng';
import type { AffinityGames, AffinityGamesResult, ChampionsResult, CompetitionResult, ElementType, KingsCupDraw, Player, SaveGame } from './types';

export const AFFINITY_ELEMENTS: ElementType[] = ['FIRE', 'WIND', 'FOREST', 'MOUNTAIN'];
const POSITION_ORDER: Player['position'][] = ['GK', 'DF', 'MF', 'FW'];
/** The catalogue has thousands of players. Index it once so opening a
 * competition does not repeatedly scan the whole dataset for each element. */
const affinityCatalog = new Map<ElementType, Player[]>(AFFINITY_ELEMENTS.map((element) => [element, [...playerById.values()].filter((player) => player.element === element)]));
/** Saves are immutable state snapshots. Reusing a generated selection within
 * the same snapshot avoids rebuilding the four national teams several times
 * while Calendar, Dashboard and competition widgets render together. */
const affinityTeamsCache = new WeakMap<SaveGame, RivalTeam[]>();
export const AFFINITY_USER_TEAM_ID = 'team_affinity_manager';
export const KINGS_CUP_COMPETITION_ID = 'kings_cup';
export const AFFINITY_GAMES_COMPETITION_ID = 'affinity_games';

export const affinityLabel = (element: ElementType) => ({
  FIRE: 'Fuego', WIND: 'Viento', FOREST: 'Bosque', MOUNTAIN: 'Montaña',
})[element];

export const affinityTokenId = (element: ElementType) => `token_${element.toLowerCase()}`;
export const affinityTeamId = (element: ElementType) => `team_affinity_${element.toLowerCase()}`;
export const affinityTeamName = (element: ElementType) => `Selección ${affinityLabel(element)}`;
export const isAffinityGamesSeason = (season: number) => season > 0 && season % 4 === 0;

const addDays = (value: string, days: number) => {
  const date = new Date(`${value}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/** The Cup picks the first-ranked side of each regional league, then ranks
 * those champions by the same visible standing tie-breakers. */
export function createKingsCupDraw(groups: LeagueGroup[], results: CompetitionResult[], season: number, seed: number, pointAdjustments: Record<string, number> = {}): KingsCupDraw | null {
  const champions = groups
    .map((group) => createStandings(group, results, pointAdjustments)[0])
    .filter(Boolean)
    .sort((left, right) => right.points - left.points
      || right.goalDifference - left.goalDifference
      || right.goalsFor - left.goalsFor
      || right.team.rating - left.team.rating
      || left.team.name.localeCompare(right.team.name));
  if (champions.length < 2) return null;
  return {
    season,
    sourceSeason: season - 1,
    seed,
    // A Saturday evening before the regular calendar settles in gives this
    // prestige final its own clear window and neutral presentation.
    date: addDays(`${2025 + season}-09-01`, 8),
    firstTeamId: champions[0].team.id,
    secondTeamId: champions[1].team.id,
  };
}

/** Creates the every-four-season event. The manager receives a randomly
 * assigned affinity, while its player pool remains a separate national side
 * rather than a mutation of the club roster. Every eligible player is exposed
 * to the manager: no random shortlist decides their national-team choices. */
export function createAffinityGames(season: number, seed: number): AffinityGames | null {
  if (!isAffinityGamesSeason(season)) return null;
  const rng = new SeededRng(seed ^ 0x41ff17);
  const element = AFFINITY_ELEMENTS[rng.int(0, AFFINITY_ELEMENTS.length - 1)];
  const firstMatchDate = addDays(`${2025 + season}-09-01`, 50);
  return {
    season,
    seed,
    element,
    announcementDate: addDays(firstMatchDate, -10),
    firstMatchDate,
    candidatePlayerIds: affinityEligiblePlayers(element).map((player) => player.id),
    squadPlayerIds: [],
    formationId: 'formation_4_4_2',
    lineupPlayerIds: [],
    benchPlayerIds: [],
    tacticalRoleOverrides: {},
    presentationComplete: false,
  };
}

/** Complete manager-facing pool for an element. `playerById` is used rather
 * than a sampled list so old and new saves expose the same full catalogue. */
export function affinityEligiblePlayers(element: ElementType, save?: SaveGame): Player[] {
  return [...(affinityCatalog.get(element) ?? [])]
    .map((player) => resolveAffinityPlayer(save, player))
    .sort((left, right) => POSITION_ORDER.indexOf(left.position) - POSITION_ORDER.indexOf(right.position)
      || playerRating(right) - playerRating(left) || left.name.localeCompare(right.name));
}

/**
 * Affinity Games borrow actual player copies from the current career world.
 * A manager-owned player represents their club-developed copy; everyone else
 * carries the development earned by their original team. This prevents a
 * national call-up from silently reverting a player to catalogue statistics.
 */
export function resolveAffinityPlayer(save: SaveGame | undefined, playerOrId: Player | string): Player | undefined {
  const player = typeof playerOrId === 'string' ? playerById.get(playerOrId) : playerOrId;
  if (!player || !save) return player;
  const isClubCopy = save.ownedPlayerIds.includes(player.id);
  return resolveDevelopedPlayer(player, isClubCopy ? save.clubDevelopment[player.id] : save.worldDevelopment[player.id]);
}

/** A recognisable squad pool: first preserve enough specialists for a real
 * 22-player selection, then add the highest-rated remaining profiles in a
 * deterministic shuffled order. */
export function affinityCandidates(element: ElementType, seed: number, size = 34, save?: SaveGame): Player[] {
  const candidates = uniquePlayers(affinityEligiblePlayers(element, save));
  const rng = new SeededRng(seed ^ elementSeed(element));
  const shuffled = [...candidates].sort((left, right) => playerRating(right) - playerRating(left));
  // Randomise small rating bands so every four-year edition feels distinct
  // without ever compromising the goalkeeper / outfield balance.
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swap = rng.int(0, index);
    if (Math.abs(playerRating(shuffled[index]) - playerRating(shuffled[swap])) < 7) {
      [shuffled[index], shuffled[swap]] = [shuffled[swap], shuffled[index]];
    }
  }
  const selected: Player[] = [];
  const take = (position: Player['position'], count: number) => {
    shuffled.filter((player) => player.position === position).slice(0, count).forEach((player) => {
      if (!selected.some((entry) => entry.id === player.id)) selected.push(player);
    });
  };
  take('GK', 3); take('DF', 9); take('MF', 10); take('FW', 8);
  shuffled.forEach((player) => {
    if (selected.length < size && !selected.some((entry) => entry.id === player.id)) selected.push(player);
  });
  return selected.slice(0, Math.min(size, selected.length));
}

/** Default choices are balanced but the manager is free to replace them in
 * the call-up screen. The helper also protects legacy or sparse data. */
export function defaultAffinitySquad(candidateIds: string[], save?: SaveGame): string[] {
  const candidates = candidateIds.flatMap((id) => resolveAffinityPlayer(save, id) ?? []);
  const selected: Player[] = [];
  const take = (positions: Player['position'][], count: number) => {
    candidates.filter((player) => positions.includes(player.position)).forEach((player) => {
      if (selected.filter((entry) => positions.includes(entry.position)).length < count
        && !selected.some((entry) => entry.id === player.id)) selected.push(player);
    });
  };
  take(['GK'], 2);
  take(['DF'], 7);
  take(['MF'], 8);
  take(['FW'], 5);
  candidates.forEach((player) => {
    if (selected.length < 22 && !selected.some((entry) => entry.id === player.id)) selected.push(player);
  });
  return selected.slice(0, 22).map((player) => player.id);
}

export function affinityLineup(ids: string[], save?: SaveGame): string[] | null {
  const playersByPosition = ids.flatMap((id) => resolveAffinityPlayer(save, id) ?? []);
  const used = new Set<string>();
  const take = (positions: Player['position'][]) => {
    const player = playersByPosition.find((entry) => positions.includes(entry.position) && !used.has(entry.id));
    if (player) used.add(player.id);
    return player?.id;
  };
  const lineup = [
    take(['GK']),
    take(['DF', 'MF']), take(['DF', 'MF']), take(['DF', 'MF']), take(['DF', 'MF']),
    take(['MF', 'DF', 'FW']), take(['MF', 'DF', 'FW']), take(['MF', 'FW', 'DF']), take(['MF', 'FW', 'DF']),
    take(['FW', 'MF']), take(['FW', 'MF']),
  ];
  return lineup.every(Boolean) ? lineup as string[] : null;
}

export type AffinityRivalSelection = {
  formationId: string;
  lineupPlayerIds: string[];
  benchPlayerIds: string[];
  squadPlayerIds: string[];
};

/**
 * Builds a stable national side for an AI affinity. The formation is random
 * per tournament edition, then each slot takes the strongest suitable natural
 * player. Reserve quotas keep the squad cover-balanced instead of letting a
 * stack of elite goalkeepers consume every place.
 */
export function selectAffinityRival(element: ElementType, seed: number, save?: SaveGame): AffinityRivalSelection {
  const rng = new SeededRng(seed ^ elementSeed(element) ^ 0xaff117);
  const formation = formations[rng.int(0, formations.length - 1)];
  const available = [...affinityEligiblePlayers(element, save)];
  const take = (role: string) => {
    const primary = positionForRole(role);
    const eligible = available.filter((player) => player.position === primary || fallbackPositionsForRole(role).includes(player.position));
    const choice = eligible.sort((left, right) => affinitySlotScore(right, role) - affinitySlotScore(left, role)
      || playerRating(right) - playerRating(left) || left.name.localeCompare(right.name))[0];
    if (!choice) return undefined;
    available.splice(available.findIndex((player) => player.id === choice.id), 1);
    return choice;
  };
  const starting = formation.roles.map(take).filter((player): player is Player => Boolean(player));
  while (starting.length < 11 && available.length) starting.push(available.shift()!);

  const required = formation.roles.reduce<Record<Player['position'], number>>((counts, role) => {
    counts[positionForRole(role)] += 1;
    return counts;
  }, { GK: 0, DF: 0, MF: 0, FW: 0 });
  const preferredSquadCount: Record<Player['position'], number> = {
    GK: 2,
    DF: Math.max(6, required.DF + 2),
    MF: Math.max(6, required.MF + 2),
    FW: Math.max(5, required.FW + 2),
  };
  const hardCap: Record<Player['position'], number> = { GK: 2, DF: 8, MF: 8, FW: 7 };
  const squad = [...starting];
  const count = (position: Player['position']) => squad.filter((player) => player.position === position).length;
  for (const position of POSITION_ORDER) {
    while (squad.length < 22 && count(position) < preferredSquadCount[position]) {
      const reserve = takeReserve(available, position);
      if (!reserve) break;
      squad.push(reserve);
    }
  }
  while (squad.length < 22 && available.length) {
    const reserve = [...available]
      .filter((player) => count(player.position) < hardCap[player.position])
      .sort((left, right) => playerRating(right) - playerRating(left) || left.name.localeCompare(right.name))[0]
      ?? [...available].sort((left, right) => playerRating(right) - playerRating(left) || left.name.localeCompare(right.name))[0];
    if (!reserve) break;
    available.splice(available.findIndex((player) => player.id === reserve.id), 1);
    squad.push(reserve);
  }
  const lineupPlayerIds = starting.slice(0, 11).map((player) => player.id);
  const squadPlayerIds = squad.slice(0, 22).map((player) => player.id);
  return {
    formationId: formation.id,
    lineupPlayerIds,
    benchPlayerIds: squadPlayerIds.filter((id) => !lineupPlayerIds.includes(id)),
    squadPlayerIds,
  };
}

export function affinityTeams(save: SaveGame): RivalTeam[] {
  const cached = affinityTeamsCache.get(save);
  if (cached) return cached;
  const event = save.affinityGames;
  if (!event) return [];
  const teams = AFFINITY_ELEMENTS.map((element) => {
    const isManager = event.element === element;
    // Each AI side rolls its tactical identity once per edition. The returned
    // IDs are deterministic, so calendar views, match day and standings all
    // refer to the exact same selection.
    const rivalSelection = selectAffinityRival(element, event.seed + 401, save);
    const ids = isManager && event.squadPlayerIds.length
      ? event.squadPlayerIds
      : rivalSelection.squadPlayerIds;
    const roster = ids.flatMap((id) => resolveAffinityPlayer(save, id) ?? []);
    const lineupPlayerIds = isManager && event.squadPlayerIds.length
      ? event.lineupPlayerIds.length === 11 ? event.lineupPlayerIds : affinityLineup(ids, save) ?? ids.slice(0, 11)
      : rivalSelection.lineupPlayerIds;
    const benchPlayerIds = isManager && event.squadPlayerIds.length
      ? ids.filter((id) => !lineupPlayerIds.includes(id))
      : rivalSelection.benchPlayerIds;
    const starting = lineupPlayerIds.flatMap((id) => roster.find((player) => player.id === id) ?? []);
    const rating = starting.length ? Math.round(starting.reduce((total, player) => total + playerRating(player), 0) / starting.length) : 60;
    return {
      id: isManager ? AFFINITY_USER_TEAM_ID : affinityTeamId(element),
      name: isManager ? `${save.clubName} · ${affinityLabel(element)}` : affinityTeamName(element),
      games: ['Affinity Games'],
      players: roster,
      rating,
      formation_id: isManager ? event.formationId : rivalSelection.formationId,
      lineupPlayerIds,
      benchPlayerIds,
    };
  });
  affinityTeamsCache.set(save, teams);
  return teams;
}

export type AffinityFixturePlan = {
  id: string;
  phase: 'GROUP' | 'FINAL';
  date: string;
  homeId: string;
  awayId: string;
  round: number;
};

export function affinityFixtures(save: SaveGame): AffinityFixturePlan[] {
  const event = save.affinityGames;
  if (!event) return [];
  const ids = affinityTeams(save).map((team) => team.id);
  const group = createDoubleRoundRobinSchedule(ids).slice(0, 3);
  const fixtures = group.flatMap((matchday, roundIndex) => matchday.fixtures.map((fixture, fixtureIndex) => ({
    id: `affinity_group_${roundIndex + 1}_${fixtureIndex + 1}`,
    phase: 'GROUP' as const,
    date: addDays(event.firstMatchDate, roundIndex * 7),
    homeId: fixture.homeId,
    awayId: fixture.awayId,
    round: roundIndex + 1,
  })));
  const groupResults = save.affinityGamesResults.filter((result) => result.phase === 'GROUP');
  if (groupResults.length < fixtures.length) return fixtures;
  const finalists = affinityStandings(save).slice(0, 2);
  return [...fixtures, {
    id: 'affinity_final', phase: 'FINAL', date: addDays(event.firstMatchDate, 25),
    homeId: finalists[0]?.teamId ?? '', awayId: finalists[1]?.teamId ?? '', round: 4,
  } as AffinityFixturePlan].filter((fixture) => fixture.homeId && fixture.awayId);
}

export function affinityStandings(save: SaveGame) {
  const teams = affinityTeams(save);
  const rows = new Map(teams.map((team) => [team.id, { teamId: team.id, team, played: 0, won: 0, drawn: 0, lost: 0, goalsFor: 0, goalsAgainst: 0, points: 0 }]));
  save.affinityGamesResults.filter((result) => result.phase === 'GROUP').forEach((result) => {
    const home = rows.get(result.homeId); const away = rows.get(result.awayId);
    if (!home || !away) return;
    home.played += 1; away.played += 1;
    home.goalsFor += result.homeScore; home.goalsAgainst += result.awayScore;
    away.goalsFor += result.awayScore; away.goalsAgainst += result.homeScore;
    if (result.homeScore > result.awayScore) { home.won += 1; home.points += 3; away.lost += 1; }
    else if (result.homeScore < result.awayScore) { away.won += 1; away.points += 3; home.lost += 1; }
    else { home.drawn += 1; away.drawn += 1; home.points += 1; away.points += 1; }
  });
  return [...rows.values()].sort((left, right) => right.points - left.points
    || (right.goalsFor - right.goalsAgainst) - (left.goalsFor - left.goalsAgainst)
    || right.goalsFor - left.goalsFor || right.team.rating - left.team.rating || left.team.name.localeCompare(right.team.name));
}

export function affinityResultFor(save: SaveGame, fixtureId: string) {
  return save.affinityGamesResults.find((result) => result.fixtureId === fixtureId);
}

export function isAffinityUserFixture(fixture: Pick<AffinityFixturePlan, 'homeId' | 'awayId'>) {
  return fixture.homeId === AFFINITY_USER_TEAM_ID || fixture.awayId === AFFINITY_USER_TEAM_ID;
}

export function neutralWinner(homeId: string, awayId: string, homeScore: number, awayScore: number, seed: number) {
  if (homeScore !== awayScore) return { winnerId: homeScore > awayScore ? homeId : awayId };
  const rng = new SeededRng(seed ^ 0x0fa11e);
  let homePenalties = 0; let awayPenalties = 0;
  for (let kick = 0; kick < 5; kick += 1) {
    homePenalties += rng.next() < .72 ? 1 : 0;
    awayPenalties += rng.next() < .72 ? 1 : 0;
  }
  while (homePenalties === awayPenalties) {
    homePenalties += rng.next() < .72 ? 1 : 0;
    awayPenalties += rng.next() < .72 ? 1 : 0;
  }
  return { winnerId: homePenalties > awayPenalties ? homeId : awayId, penaltyHomeScore: homePenalties, penaltyAwayScore: awayPenalties };
}

function uniquePlayers(source: Player[]) {
  const seen = new Set<string>();
  return source.filter((player) => {
    const key = `${player.name.toLocaleLowerCase()}::${player.position}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function elementSeed(element: ElementType) {
  return ({ FIRE: 0x11, WIND: 0x22, FOREST: 0x33, MOUNTAIN: 0x44 })[element];
}

/** Converts a formation slot into the broad catalogue position. The source
 * data uses detailed football roles, whereas player cards use four groups. */
function positionForRole(role: string): Player['position'] {
  if (role === 'GK') return 'GK';
  if (['LB', 'RB', 'CB', 'SW', 'LWB', 'RWB'].includes(role)) return 'DF';
  if (['DM', 'CM', 'AM', 'LM', 'RM'].includes(role)) return 'MF';
  return 'FW';
}

/** A small fallback keeps sparse elemental pools fieldable without allowing
 * absurd choices such as a fifth goalkeeper as an attacking substitute. */
function fallbackPositionsForRole(role: string): Player['position'][] {
  switch (positionForRole(role)) {
    case 'DF': return ['MF'];
    case 'MF': return ['DF', 'FW'];
    case 'FW': return ['MF'];
    default: return [];
  }
}

/** General rating decides most ties, while the relevant attributes decide
 * which equally strong player best fits each individual formation slot. */
function affinitySlotScore(player: Player, role: string) {
  const stats = player.stats;
  const rating = playerRating(player);
  if (role === 'GK') return rating * 1.35 + stats.defense * .28 + stats.control * .22 + stats.power * .1;
  if (['LB', 'RB', 'LWB', 'RWB'].includes(role)) return rating * 1.25 + stats.defense * .18 + stats.speed * .18 + stats.pass * .12;
  if (['CB', 'SW'].includes(role)) return rating * 1.25 + stats.defense * .28 + stats.power * .16 + stats.control * .08;
  if (['DM', 'CM', 'AM', 'LM', 'RM'].includes(role)) return rating * 1.25 + stats.pass * .2 + stats.control * .17 + stats.dribble * .12;
  return rating * 1.25 + stats.kick * .24 + stats.dribble * .18 + stats.speed * .14;
}

function takeReserve(available: Player[], position: Player['position']) {
  const reserve = available
    .filter((player) => player.position === position)
    .sort((left, right) => playerRating(right) - playerRating(left) || left.name.localeCompare(right.name))[0];
  if (!reserve) return undefined;
  available.splice(available.findIndex((player) => player.id === reserve.id), 1);
  return reserve;
}
